import React, { useContext } from 'react';
import {
  Button,
  CircularProgress,
  Box,
  makeStyles,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TablePagination,
  Checkbox,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import RemoveIcon from '@material-ui/icons/Remove';
import { useCustomReportSearchModal } from './useCustomReportSearchModal';
import { JCdTransactionsContext } from 'src/views/trans/JCdTrans/useJCdTransContext';

const useStyles = makeStyles(theme => ({
  content: {
    padding: theme.spacing(2)
  },
  tableHeader: {
    backgroundColor: '#f57c00'
  },
  tableHeaderCell: {
    color: 'white',
    fontWeight: 'bold'
  },
  searchField: {
    marginBottom: theme.spacing(2),
    width: '100%'
  },
  modalContent: {
    padding: theme.spacing(2)
  }
}));

const CustomReportSearchModalTable = () => {
  const classes = useStyles();
  const context = useContext(JCdTransactionsContext);
  const {
    loading,
    modalOpen,
    filteredReportData,
    modalFilteredData,
    reportDefinition,
    page,
    rowsPerPage,
    buttonLabel,
    disableActionButton,
    buttonVariant,
    buttonColor,
    handleOpenModal,
    handleCloseModal,
    handleSearch,
    searchTerm,
    handleChangePage,
    handleChangeRowsPerPage,
    selectedItems,
    toggleItemSelection,
    clearSelection,
    handleAddSelectedItems,
    adding,
    isViewMode,
    fieldKey,
    handleAssignItems,
    handleUnassignItems,
    allow_unassign,
    handleRemoveItem
  } = useCustomReportSearchModal();

  // Get persisted data from context using the fieldKey
  const persistedReportData = context?.transactionsData?.kvs?.[fieldKey]?.value || [];

  const normalizeToArray = value => {
    if (typeof value === 'string') {
      const normalized = value.trim();
      if (normalized === '') return [];
      try {
        const parsed = JSON.parse(normalized);
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch (error) {
        return [];
      }
    }
    if (Array.isArray(value)) return value;
    if (value === undefined || value === null || value === '') return [];
    return [value];
  };

  // Use filteredReportData in view mode, otherwise use persisted data
  const displayData = isViewMode ? filteredReportData : normalizeToArray(persistedReportData || []);

  // Modal-specific selection handling
  const handleModalSelectAll = event => {
    if (event.target.checked) {
      const allIds = modalFilteredData.map(row => row.sub_id);
      allIds.forEach(id => toggleItemSelection(id));
    } else {
      clearSelection();
    }
  };

  const isModalAllSelected = modalFilteredData.length > 0 && selectedItems.length === modalFilteredData.length;
  const isModalIndeterminate = selectedItems.length > 0 && selectedItems.length < modalFilteredData.length;

  return (
    <>
      <Box className={classes.content}>
        {reportDefinition ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow className={classes.tableHeader}>
                  {!isViewMode && (
                    <TableCell className={classes.tableHeaderCell} style={{ width: 50 }}>
                      Actions
                    </TableCell>
                  )}
                  {reportDefinition.jsonSettings?.headers &&
                    Object.entries(reportDefinition.jsonSettings.headers).map(
                      ([key, headerCaption], index) => (
                        <TableCell
                          key={index}
                          className={classes.tableHeaderCell}
                        >
                          {headerCaption}
                        </TableCell>
                      )
                    )}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(displayData) && displayData.length > 0 ? (
                  displayData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => (
                      <TableRow key={row.sub_id || index}>
                        {!isViewMode && (
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveItem(row.sub_id)}
                              disabled={adding}
                            >
                              <RemoveIcon />
                            </IconButton>
                          </TableCell>
                        )}
                        {reportDefinition.jsonSettings?.headers &&
                          Object.keys(reportDefinition.jsonSettings.headers).map((key, colIndex) => (
                            <TableCell key={colIndex}>{row[key]}</TableCell>
                          ))}
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={
                        reportDefinition.jsonSettings?.headers
                          ? Object.keys(reportDefinition.jsonSettings.headers).length + (isViewMode ? 0 : 1)
                          : isViewMode ? 1 : 2
                      }
                      align="center"
                    >
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box p={2}>
            No report definition available
          </Box>
        )}

        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" gap={2}>
            {!isViewMode && (
              <Button
                onClick={handleOpenModal}
                disabled={disableActionButton}
                variant={buttonVariant}
                color={buttonColor}
                size="small"
              >
                {buttonLabel}
              </Button>
            )}
            {!isViewMode && Array.isArray(displayData) && displayData.length > 0 && (
              <>
                <Button
                  onClick={allow_unassign ? handleUnassignItems : handleAssignItems}
                  disabled={adding}
                  variant="contained"
                  color="primary"
                  size="small"
                  style={{ display: 'none' }}
                  id="hidden-assign-all-button"
                >
                  {adding 
                    ? (allow_unassign ? 'Unassigning...' : 'Assigning...') 
                    : `${allow_unassign ? 'Unassign' : 'Assign'} All`}
                </Button>
              </>
            )}
          </Box>
          <Box>
            <TablePagination
              component="div"
              count={Array.isArray(displayData) ? displayData.length : 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
            />
          </Box>
        </Box>
      </Box>

      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          style: { maxHeight: '80vh' }
        }}
      >
        <DialogTitle>{buttonLabel}</DialogTitle>
        <DialogContent className={classes.modalContent}>
          <TextField
            className={classes.searchField}
            placeholder="Search items..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            disabled={loading}
          />

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : reportDefinition ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow className={classes.tableHeader}>
                    <TableCell className={classes.tableHeaderCell} style={{ width: 50 }}>
                      <Checkbox
                        indeterminate={isModalIndeterminate}
                        checked={isModalAllSelected}
                        onChange={handleModalSelectAll}
                        disabled={loading || adding}
                      />
                    </TableCell>
                    {reportDefinition.jsonSettings?.headers &&
                      Object.entries(reportDefinition.jsonSettings.headers).map(
                        ([key, headerCaption], index) => (
                          <TableCell
                            key={index}
                            className={classes.tableHeaderCell}
                          >
                            {headerCaption}
                          </TableCell>
                        )
                      )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(modalFilteredData) &&
                    modalFilteredData
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row, index) => (
                        <TableRow key={row.sub_id || index}>
                          <TableCell>
                            <Checkbox
                              checked={selectedItems.includes(row.sub_id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleItemSelection(row.sub_id);
                              }}
                              disabled={loading || adding}
                            />
                          </TableCell>
                          {reportDefinition.jsonSettings?.headers &&
                            Object.keys(reportDefinition.jsonSettings.headers).map((key, colIndex) => (
                              <TableCell key={colIndex}>{row[key]}</TableCell>
                            ))}
                        </TableRow>
                      ))}
                  {(!Array.isArray(modalFilteredData) || modalFilteredData.length === 0) && (
                    <TableRow>
                      <TableCell
                        colSpan={
                          reportDefinition.jsonSettings?.headers
                            ? Object.keys(reportDefinition.jsonSettings.headers).length + 1
                            : 2
                        }
                        align="center"
                      >
                        {searchTerm ? 'No matching results found' : 'No data available'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Box display="flex" justifyContent="flex-end">
                <TablePagination
                  component="div"
                  count={Array.isArray(filteredReportData) ? filteredReportData.length : 0}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                />
              </Box>
            </TableContainer>
          ) : (
            <Box p={2}>
              No report definition available
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={adding}>
            Cancel
          </Button>
          {selectedItems.length > 0 && (
            <Button
              onClick={clearSelection}
              variant="outlined"
              size="small"
              disabled={adding}
              style={{ marginRight: 8 }}
            >
              Clear Selection ({selectedItems.length})
            </Button>
          )}
          <Button
            onClick={handleAddSelectedItems}
            disabled={selectedItems.length === 0 || adding}
            variant="contained"
            color="primary"
          >
            {adding ? 'Adding...' : `Add Selected (${selectedItems.length})`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomReportSearchModalTable;